import { useMutation, useQuery } from "@apollo/client";
import { ResponseStatusEnum, UrnTypeEnum } from "@clockwise/schema";
import { H2 } from "@components/ui";
import { Check, KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { Skeleton } from "@material-ui/lab";
import { niceName, roomString } from "@src/util/event.util";
import classNames from "classnames";
import { compact, find, isEmpty } from "lodash";
import React, { useState, useEffect } from "react";
import {
  EventSchedulingDocument,
  EventSchedulingQuery,
} from "./__generated__/EventScheduling.generated";
import { getRenderTimeZone } from "@src/util/time-zone.util";
import { SuggestedRescheduleSlotsForEventDocument } from "./__generated__/SuggestedRescheduleSlotsForEvent.generated";
import { EScheduleRange, getMaxMinSlotForRange } from "@src/util/scheduler.util";
import RescheduleButton from "@components/event/RescheduleButton";
import { RescheduleEventDocument } from "./__generated__/RescheduleEvent.generated";
import { getEventTimeForSchedulingSlot } from "@src/util/event-time.util";
import { setTimeout } from "timers";
import { motion } from "framer-motion";
import { CircularProgress } from "@material-ui/core";
import moment from "moment-timezone";
import { getCurrentWorkingGroup } from "@src/util/working-group.util";
import EventAttendeesAvatars from "./EventAttendeesAvatars";
import AttendeesSlotPainsSummary from "./AttendeesSlotPainsSummary";
import AttendeesSlotPains from "./AttendeesSlotPains";
import EventSlot from "./EventSlot";
import { getValue, isValue } from "@clockwise/web-commons/src/util/errorable.util";
import { track, TrackingEvents } from "@clockwise/web-commons/src/util/analytics.util";
import { eventSurpassesMaxAttendeesForSuggestedSlots } from "@components/event/Event";

type Props = {
  orgId: string;
  externalEventId: string;
  eventCalendarId: string;
  enableRescheduling: boolean;
  disableReschedulingReason: JSX.Element | null;
  hidePains?: boolean;
};

export const eventIconClasses = "cw-h-4 cw-w-4 cw-mr-2 cw-flex cw-shrink-0 cw-mt-0.5";
export const baseClasses =
  "cw-flex cw-gap-2 cw-text-13 cw-leading-4 cw-text-slate-standard cw-items-start";
export const rowClasses = `${baseClasses} cw-flex-row`;

export type EventSchedulingQueryOrg = Extract<EventSchedulingQuery["org"], { __typename: "Org" }>;
type ForceFetchEventParentErrorable = EventSchedulingQueryOrg["forceFetchEventParentErrorable"];
type ComputeCurrentSlotImpactErrorable = EventSchedulingQueryOrg["computeCurrentSlotImpactErrorable"];
type CurrentScheduledSlotImpact = Extract<
  ComputeCurrentSlotImpactErrorable,
  { __typename: "CurrentScheduledSlotImpact" }
>;
export type EventSchedulingQueryEventParent = Extract<
  ForceFetchEventParentErrorable,
  { __typename: "EventParent" }
>;
export type EventSchedulingQueryEvent = EventSchedulingQueryEventParent["events"][number];
export type EventSchedulingQueryAttendee = EventSchedulingQueryEvent["attendees"][number];
export type EventSchedulingQueryPerson = Extract<
  EventSchedulingQueryEvent["attendeePeople"],
  { __typename: "PersonList" }
>["list"][number];

type EventSchedulingQueryCurrentSlot = Extract<
  CurrentScheduledSlotImpact["currentSlot"],
  { __typename: "SchedulingSlot" }
>;
type EventSchedulingQuerySchedulingAttendanceImpact = EventSchedulingQueryCurrentSlot["attendanceImpacts"][number];
export type EventSchedulingQueryImpact = EventSchedulingQuerySchedulingAttendanceImpact;

export interface PersonImpact {
  impacts?: EventSchedulingQueryImpact;
  calendarId: string;
}

export type PersonMap = {
  [email: string]: EventSchedulingQueryPerson;
};
export type AttendeeMap = Record<string, EventSchedulingQueryAttendee>;

export type EventSchedulingOrg = Extract<EventSchedulingQuery["org"], { __typename: "Org" }>;

export const getOrderedAttendeesAndPersonsMaps = ({
  org,
  event,
}: {
  org: EventSchedulingOrg;
  event: EventSchedulingQueryEvent;
}): {
  personImpacts: PersonImpact[];
  personMap: PersonMap;
  attendeeMap: AttendeeMap;
} => {
  let personImpacts: PersonImpact[];

  // on errors, we will generate the view via the event attendees
  const currentScheduledSlotImpact = getValue(org?.computeCurrentSlotImpactErrorable);
  const currentSlot = getValue(currentScheduledSlotImpact?.currentSlot);

  if (!org || !currentScheduledSlotImpact || !currentSlot) {
    const attendeesMap = event.attendees
      .map((attendee) => ({ calendarId: attendee.urnValue }))
      .filter(({ calendarId }) => calendarId.indexOf(roomString) === -1);
    const calendarIdsMap = event.calendarIds.map((calendarId) => ({ calendarId }));

    // if list of attendees is empty eg in the case of holds, fallback to calendarIds
    personImpacts = event.attendees.length > 0 ? attendeesMap : calendarIdsMap;
  } else {
    const { uncrawledCalendars } = currentScheduledSlotImpact;

    const { attendanceImpacts } = currentSlot;

    personImpacts = attendanceImpacts
      .filter((ai) => {
        // filter out rooms
        return ai.calendarId.indexOf(roomString) === -1;
      })
      .sort((a, b) => {
        // sort most focus time cost first
        const aScore = -a.focusTimeMinutesCost / 60;
        const bScore = -b.focusTimeMinutesCost / 60;
        return aScore - bScore;
      })
      .map((impacts) => ({ impacts, calendarId: impacts.calendarId }));

    personImpacts.push.apply(
      personImpacts,
      compact(uncrawledCalendars || [])
        .filter((calendarId) => {
          // filter out rooms
          return calendarId.indexOf(roomString) === -1;
        })
        .map((calendarId) => ({ calendarId })),
    );
  }

  const personMap: { [email: string]: EventSchedulingQueryPerson } = {};
  const attendeeMap: Record<string, EventSchedulingQueryAttendee> = {};
  event.attendees.forEach((attendee) => (attendeeMap[attendee.urnValue] = attendee));

  let personList: EventSchedulingQueryPerson[] = [];
  const attendeePeople = getValue(event?.attendeePeople);
  if (attendeePeople) {
    personList = attendeePeople.list;
    personList.forEach((person) => {
      personMap[person.primaryEmail] = person;
    });
  }

  personImpacts = personImpacts.sort((a, b) => {
    const aAttendee = attendeeMap[a.calendarId] || null;
    const bAttendee = attendeeMap[b.calendarId] || null;
    const aPerson = personMap[a.calendarId] || null;
    const bPerson = personMap[b.calendarId] || null;

    type Person = typeof aPerson;
    const alphabetizeByFirstNameLastName = (
      aPerson: Person | null,
      bPerson: Person | null,
    ): number => {
      // Returns a number value (a compareFn value) that can used to sort strings alphabetically
      return niceName(aPerson, a.calendarId, true).localeCompare(
        niceName(bPerson, b.calendarId, true),
      );
    };

    if (aAttendee && aAttendee.organizer) {
      return -1;
    } else if (bAttendee && bAttendee.organizer) {
      return 1;
    }

    // Then put those with conflicts
    const aPersonHasConflict = a.impacts?.hasConflict;
    const bPersonHasConflict = b.impacts?.hasConflict;
    if (aPersonHasConflict && !bPersonHasConflict) {
      return -1;
    } else if (bPersonHasConflict && !aPersonHasConflict) {
      return 1;
    } else if (aPersonHasConflict && bPersonHasConflict) {
      return alphabetizeByFirstNameLastName(aPerson, bPerson);
    }

    // Then put those with inconveniences
    const aPersonHasInconveniences = !isEmpty(a.impacts?.inconveniences);
    const bPersonHasInconveniences = !isEmpty(b.impacts?.inconveniences);
    if (aPersonHasInconveniences && !bPersonHasInconveniences) {
      return -1;
    } else if (bPersonHasInconveniences && !aPersonHasInconveniences) {
      return 1;
    } else if (aPersonHasInconveniences && bPersonHasInconveniences) {
      return alphabetizeByFirstNameLastName(aPerson, bPerson);
    }

    // The put those who have RSVP-ed
    const aPersonHasRSVPed = aAttendee?.responseStatus == ResponseStatusEnum.Accepted;
    const bPersonHasRSVPed = bAttendee?.responseStatus == ResponseStatusEnum.Accepted;
    if (aPersonHasRSVPed && !bPersonHasRSVPed) {
      return -1;
    } else if (bPersonHasRSVPed && !aPersonHasRSVPed) {
      return 1;
    }

    // If none of the above apply, then alphabetize
    return alphabetizeByFirstNameLastName(aPerson, bPerson);
  });

  return {
    personImpacts,
    personMap,
    attendeeMap,
  };
};

const MAX_NUM_OF_SUGGESTED_SLOTS = 3;

export const attendeesHaveConflict = (
  currentSlot:
    | {
        summary: {
          conflictingAttendees: string[];
        };
      }
    | null
    | undefined,
) => {
  const summary = currentSlot?.summary;
  const conflictingAttendees = summary?.conflictingAttendees;
  return !isEmpty(conflictingAttendees);
};

type ShowMoreShowLessProps = {
  isExpanded: boolean;
  handleClick: () => void;
};
const ShowMoreShowLess = ({ isExpanded, handleClick }: ShowMoreShowLessProps) => {
  return (
    <div className={rowClasses}>
      <div
        className="cw-flex cw-flex-row cw-font-bold cw-cursor-pointer cw-gap-2"
        onClick={handleClick}
        cw-id="scheduling-summary-toggle"
      >
        {isExpanded ? (
          <KeyboardArrowUp className="cw-text-sm cw-mr-2" />
        ) : (
          <KeyboardArrowDown className="cw-text-sm cw-mr-2" />
        )}
        {isExpanded ? "Show less" : "Show more"}
      </div>
    </div>
  );
};

export function EventScheduling({
  orgId,
  externalEventId,
  eventCalendarId,
  enableRescheduling,
  disableReschedulingReason,
}: Props) {
  const { data, loading, refetch } = useQuery(EventSchedulingDocument, {
    variables: {
      orgId,
      externalEventId,
      calendarId: eventCalendarId,
    },
    fetchPolicy: "no-cache",
  });

  const [isRescheduling, setIsRescheduling] = useState(false);
  const [selectedSlot, setSeletedSlot] = useState<number | null>(null);
  const [confirmedSlot, setConfirmedSlot] = useState<number | null>(null);
  const [wasJustRescheduled, setWasJustRescheduled] = useState(false);
  const [rescheduleEvent, { error: rescheduleError }] = useMutation(RescheduleEventDocument, {
    onCompleted: () => {
      setTimeout(() => {
        // Wait to allow `setIsRescheduling` animation to show before `setWasJustRescheduled` animations
        setIsRescheduling(false);
        setWasJustRescheduled(true);
        setTimeout(() => {
          // Wait to allow `setWasJustRescheduled` animation before refetching data (which will refresh the UI)
          // Reload the data
          refetch().then(() => {
            // reset the flag
            setWasJustRescheduled(false);
            setSeletedSlot(null);
            setConfirmedSlot(null);
          });
        }, 3500);
      }, 1000);
    },
    onError: () => {
      // reset the flag
      setIsRescheduling(false);
      setSeletedSlot(null);
      setConfirmedSlot(null);
    },
  });

  const [isExpanded, setIsExpanded] = useState(false);

  const org = getValue(data?.org, "Org");
  const computeCurrentSlotImpactErrorable = getValue(org?.computeCurrentSlotImpactErrorable);
  const currentSlot = computeCurrentSlotImpactErrorable?.currentSlot;
  const eventParent = getValue(org?.forceFetchEventParentErrorable);
  const events = eventParent?.events;
  const event = find(events, (event) => event?.eventKey?.externalEventId === externalEventId);
  const attendees = event?.attendees;
  const calendarAttendee = find(
    attendees,
    (attendee) => attendee?.urnType == UrnTypeEnum.Email && attendee?.urnValue == eventCalendarId,
  );
  const responseStatusOnCalendar = calendarAttendee?.responseStatus;

  const timeZone = getRenderTimeZone();

  const specifiedMoment = event?.startTime?.dateOrDateTime
    ? moment(event.startTime.dateOrDateTime)
    : undefined;
  const { rangeMin: reschedulerRangeMin, rangeMax: reschedulerRangeMax } = getMaxMinSlotForRange(
    EScheduleRange.SevenBeforeAndAfter,
    specifiedMoment,
  );

  // Prevents multiple queries when there's no event data yet
  // And prevents a query that would error when event had more than max attendees
  const skipQuery = !event || eventSurpassesMaxAttendeesForSuggestedSlots(event);
  const { data: eventSuggestionsData } = useQuery(SuggestedRescheduleSlotsForEventDocument, {
    variables: {
      orgId,
      externalEventId,
      timeZone,
      reschedulerRangeMin,
      reschedulerRangeMax,
    },
    fetchPolicy: "no-cache",
    skip: skipQuery,
  });

  const reschedulingOrg = getValue(eventSuggestionsData?.node, "Org");
  const reschedulingResponse = getValue(reschedulingOrg?.reschedulingResponseErrorable);
  const slots = reschedulingResponse?.slots || [];
  const hasDeclined = responseStatusOnCalendar == ResponseStatusEnum.Declined;
  const hasConflict = attendeesHaveConflict(currentSlot);
  const hasConflictButHasDeclined = hasConflict && hasDeclined; // UI depends on this state
  // Note that has not declined includes several states: Accepted, NeedsAction, Tentative
  const hasConflictButHasNotDeclined = hasConflict && !hasDeclined;

  useEffect(() => {
    if (hasConflict) {
      // If we show conflict notifications for an event, track as displayed.
      track(TrackingEvents.EVENT.SCHEDULING.CONFLICT_NOTIFICATION_DISPLAYED, {
        hasDeclined,
        externalEventId,
        hasNoSuggestions: isEmpty(slots),
      });
    }
  }, [externalEventId, hasConflict]);

  const onShowMoreShowLessClick = () => {
    const nextIsExpanded = !isExpanded;
    track(TrackingEvents.EVENT.SCHEDULING.CONFLICT_NOTIFICATION_CLICKED, {
      isExpanded: nextIsExpanded,
      externalEventId,
      hasConflict,
    });
    setIsExpanded(nextIsExpanded);
  };

  if (loading) {
    return (
      <div className="cw-whitespace-pre">
        <H2>Scheduling</H2>
        <Skeleton height={95} variant="rect" className="cw-bg-grey-lightester cw-w-full" />
      </div>
    );
  }

  const attendeePeople = getValue(event?.attendeePeople);
  const personList = attendeePeople?.list || [];

  return (
    <div className="cw-whitespace-pre">
      <H2
        className={classNames({
          "cw-text-peach-darker": hasConflictButHasNotDeclined,
          "cw-text-slate-standard": !hasConflictButHasNotDeclined,
        })}
      >
        Scheduling
      </H2>
      {!hasConflict && (
        <>
          <div className="cw-bg-grey-lightester cw-flex cw-flex-col cw-text-slate-standard cw-text-xs cw-p-4 cw-rounded cw-gap-3">
            {!isExpanded && <EventAttendeesAvatars org={org} event={event} />}
            {isExpanded && <AttendeesSlotPains org={org} event={event} />}
            {org && isValue(org.computeCurrentSlotImpactErrorable) && (
              <ShowMoreShowLess isExpanded={isExpanded} handleClick={onShowMoreShowLessClick} />
            )}
          </div>
          {eventParent && (
            <RescheduleButton
              enableRescheduling={enableRescheduling}
              disableReschedulingReason={disableReschedulingReason}
              eventParent={eventParent}
              calendarId={eventCalendarId}
              buttonText="Reschedule this meeting"
            />
          )}
        </>
      )}
      {hasConflictButHasDeclined && (
        <>
          <div className="cw-bg-grey-lightester cw-flex cw-flex-col cw-text-slate-standard cw-text-xs cw-p-4 cw-rounded cw-gap-3">
            {!isExpanded && (
              <>
                <EventAttendeesAvatars org={org} event={event} />
                <AttendeesSlotPainsSummary org={org} persons={personList} event={event} />
              </>
            )}
            {isExpanded && (
              <>
                <AttendeesSlotPains org={org} event={event} />
              </>
            )}
            <ShowMoreShowLess isExpanded={isExpanded} handleClick={onShowMoreShowLessClick} />
          </div>
          {eventParent && (
            <RescheduleButton
              enableRescheduling={enableRescheduling}
              disableReschedulingReason={disableReschedulingReason}
              eventParent={eventParent}
              calendarId={eventCalendarId}
              buttonText="Reschedule this meeting"
            />
          )}
        </>
      )}
      {hasConflictButHasNotDeclined && (
        <>
          <div className="cw-bg-peach-lightest/50 cw-flex cw-flex-col cw-text-slate-standard cw-text-xs cw-p-4 cw-rounded cw-gap-3">
            {!isExpanded && (
              <>
                <EventAttendeesAvatars org={org} event={event} />
                <AttendeesSlotPainsSummary
                  org={org}
                  persons={personList}
                  event={event}
                  highlightPains
                />
              </>
            )}
            {isExpanded && <AttendeesSlotPains org={org} event={event} />}
            <ShowMoreShowLess isExpanded={isExpanded} handleClick={onShowMoreShowLessClick} />
          </div>
          <div className={baseClasses + " cw-flex-col cw-text-sm cw-mt-0 cw-relative"}>
            {enableRescheduling && (
              <motion.div
                className={"cw-flex cw-flex-col cw-w-full cw-gap-2"}
                initial={{ height: "100%", opacity: 1 }}
                animate={{
                  height: wasJustRescheduled ? "0px" : "100%",
                  opacity: wasJustRescheduled ? 0 : 1,
                  transition: { delay: 3 },
                }}
              >
                <motion.span
                  className="cw-font-body cw-mt-2"
                  initial={{ opacity: 1 }}
                  animate={{
                    opacity: isRescheduling || wasJustRescheduled ? 0.5 : 1,
                    transition: { delay: 0.5 },
                  }}
                >
                  Suggestions
                </motion.span>
                {slots.slice(0, MAX_NUM_OF_SUGGESTED_SLOTS).map((slot, index) => {
                  const workingGroup = getCurrentWorkingGroup(org);

                  return (
                    <div
                      className={"cw-w-full cw-transition-all cw-duration-500 cw-relative"}
                      key={index}
                      onClick={() => {
                        setSeletedSlot(index);
                        track(TrackingEvents.EVENT.SCHEDULING.RESCHEDULE_SUGGESTION_CLICKED, {
                          externalEventId,
                          selectedTimeSlot: slot,
                        });
                      }}
                    >
                      {(isRescheduling || wasJustRescheduled) && index == confirmedSlot && (
                        <div className="cw-h-20 cw-flex cw-justify-center cw-items-center cw-border-2 cw-border-gray-200 cw-border-solid cw-rounded cw-shadow-sm">
                          {isRescheduling && <CircularProgress size={24} color="primary" />}
                          {wasJustRescheduled && !rescheduleError && (
                            <motion.span
                              className="cw-flex cw-items-center"
                              initial={{ fontSize: 0, opacity: 0 }}
                              animate={{ opacity: 1, transition: { delay: 1.5 }, fontSize: "1em" }}
                            >
                              <Check color="primary" /> Rescheduled!
                            </motion.span>
                          )}
                          {wasJustRescheduled && rescheduleError && (
                            <span>Something went wrong</span>
                          )}
                        </div>
                      )}
                      {
                        <motion.div
                          initial={{ opacity: 1 }}
                          animate={{
                            opacity:
                              (isRescheduling || wasJustRescheduled) && index != confirmedSlot
                                ? 0.5
                                : 1,
                            transition: { delay: 0.5 },
                          }}
                          className={classNames({
                            "cw-hidden":
                              (isRescheduling || wasJustRescheduled) && index == confirmedSlot,
                          })}
                        >
                          {
                            <EventSlot
                              slot={slot}
                              index={index}
                              workingGroup={workingGroup}
                              people={personList}
                              isSelected={selectedSlot == index}
                              onConfirm={() => {
                                if (!event || !slot) {
                                  return;
                                }

                                track(
                                  TrackingEvents.EVENT.SCHEDULING.RESCHEDULE_SUGGESTION_CONFIRMED,
                                  { externalEventId, selectedTimeSlot: slot },
                                );

                                const { timeRange } = slot;
                                if (!timeRange) {
                                  return;
                                }
                                const { startTime } = timeRange;
                                if (!startTime) {
                                  return;
                                }
                                const slotWithProperTypeInference = {
                                  ...slot,
                                  timeRange: {
                                    timeRange,
                                    startTime: {
                                      ...startTime,
                                    },
                                  },
                                };
                                const eventTime = getEventTimeForSchedulingSlot(
                                  slotWithProperTypeInference,
                                );

                                // fire off the mutation
                                setIsRescheduling(true);
                                setConfirmedSlot(index);
                                rescheduleEvent({
                                  variables: {
                                    orgId,
                                    externalEventId,
                                    ...eventTime,
                                  },
                                });
                              }}
                            />
                          }
                        </motion.div>
                      }
                    </div>
                  );
                })}
                {isEmpty(slots) && (
                  <div className="cw-flex cw-flex-col cw-gap-0.5 cw-p-4 cw-rounded cw-bg-grey-lightester">
                    <div className="cw-text-slate-standard cw-text-sm cw-font-normal">
                      No good times available
                    </div>
                    <div className="cw-text-xs cw-font-normal cw-text-[#96ACC0]">
                      Within a week of the current time
                    </div>
                  </div>
                )}
              </motion.div>
            )}
            <div className="cw-w-full">
              {eventParent && (
                <RescheduleButton
                  enableRescheduling={enableRescheduling}
                  disableReschedulingReason={disableReschedulingReason}
                  eventParent={eventParent}
                  calendarId={eventCalendarId}
                  buttonText={
                    wasJustRescheduled || !enableRescheduling
                      ? "Reschedule this meeting"
                      : "More options"
                  }
                />
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
